import React, { useEffect, useMemo, useState } from 'react'
import Prismic from 'prismic-javascript'
import { RichText } from 'prismic-reactjs'
import get from 'lodash/get'
import gsap from 'gsap'
import styled from 'styled-components'
import { Helmet } from 'react-helmet'
import { client } from '../prismic-configuration'
import NotFound from './NotFound'
import PasswordGate from '../components/PasswordGate'
import { isAuthedClient } from '../utils/auth'
import { filterExperienceForCurrentVariant } from '../utils/contentTargeting'
import { getVariantField, hasVariantField } from '../utils/siteVariant'
import {
  getThemeBackground,
  getThemeColor,
  isLightTheme,
  shouldUseSiteBackgroundImage,
} from '../utils/siteTheme'
import { addClass, removeClass } from '../utils/classie'

const MOBILE_BREAKPOINT = 1224
const MIN_HEIGHT = 38 // matches FrontMobile

const StyledPage = styled.div`
  width: 100vw;
  min-height: 100vh;
  overflow: hidden;
  background: ${(props) => props.background};
`

const StyledSiteBackground = styled.div`
  position: fixed;
  background: url(${(props) => props.background}) center center no-repeat;
  background-size: cover;
  width: 100vw;
  height: 100vh;
  z-index: -1;
`

const DesktopWrap = styled.div`
  width: 100%;
  height: 100vh;
  overflow: hidden;
`

const MobileWrap = styled.div`
  width: 100%;
  height: 100vh;
  overflow: hidden;
`

const StyledColumn = styled.div`
  background: ${(props) => props.background};
  color: ${(props) => props.color};
  padding: 0 8px;
  display: block;
  float: left;
  min-width: 62px;
  height: 100vh;
  scrollbar-width: none;
  -ms-overflow-style: none;
  overflow: auto;
  width: calc(calc(100vw - 300px) / 2);
  position: relative;

  &.column-bio {
    max-width: 300px;
    font-family: 'Phonic Regular', 'Helvetica Neue', sans-serif;

    .client {
      padding-left: 0.5rem;
    }
    .project {
      padding-left: 1rem;
    }
  }

  .border-top {
    border-top: 1px solid ${(props) => props.color};
    padding: 8px 0 0;
    margin-top: 8px;
  }

  .border-bottom {
    border-bottom: 1px solid ${(props) => props.color};
    padding: 8px 0;
    margin-bottom: 8px;
  }

  .column-inner-content {
    padding-bottom: 16px;
  }

  .column-header {
    position: sticky;
    top: 0;
    z-index: 999;
    padding: 8px 0;
    background: ${(props) => props.background};
    color: ${(props) => props.color};
    cursor: pointer;
  }

  a {
    color: inherit;
  }
`

const CvColumnDesktop = styled(StyledColumn)`
  width: calc(100vw - 300px);
  min-width: 320px;

  .cv-inner {
    padding: 8px 0 64px;
  }

  .cv-grid {
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    column-gap: 8px;
    width: 100%;
  }

  .cv-title {
    grid-column: 2 / -1;
    font-family: 'Phonic Regular', 'Helvetica Neue', sans-serif;
    font-size: 12px;
    letter-spacing: 0;
    margin-bottom: 8px;
  }

  .row-grid {
    grid-column: 2 / -1;
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    column-gap: 8px;
    border-top: 1px solid ${(props) => props.color};
    padding: 40px 0;
  }

  /* ✅ remove top border from first desktop entry too (nice parity) */
  .row-grid:first-of-type {
    border-top: none;
    padding-top: 0;
  }

  .exp-left {
    grid-column: 1 / span 2;
    font-family: 'Phonic Regular', 'Helvetica Neue', sans-serif;
    font-size: 12px;
    line-height: 1.45;
  }

  .exp-title {
    margin: 0 0 2px;
    line-height: 1.35;
  }

  .exp-company-dates {
    margin: 0 0 10px;
    opacity: 0.95;
    line-height: 1.35;
  }

  .exp-overview p {
    margin: 0;
  }

  .exp-bullets {
    grid-column: 5 / span 3;
    font-family: 'Phonic Regular', 'Helvetica Neue', sans-serif;
    font-size: 12px;
    line-height: 1.45;
  }

  .exp-bullets ul,
  .exp-bullets ol {
    margin: 0;
    padding-left: 14px;
  }

  .exp-bullets li {
    margin: 0px 10px 10px;
  }

  .exp-bullets p {
    margin: 0 0 10px;
  }
`

const MobilePanel = styled.div`
  background: ${(props) => props.background};
  color: ${(props) => props.color};
  padding: 0 8px;
  width: 100vw;
  overflow: auto;
  position: relative;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  font-family: 'Phonic Regular', 'Helvetica Neue', sans-serif;
  font-size: 12px;
  line-height: 1.35;

  .border-top {
    border-top: 1px solid ${(props) => props.color};
    padding: 8px 0 0;
    margin-top: 8px;
  }

  .border-bottom {
    border-bottom: 1px solid ${(props) => props.color};
    padding: 8px 0;
    margin-bottom: 8px;
  }

  .panel-header {
    position: sticky;
    top: 0;
    z-index: 10;
    padding: 8px 0;
    background: ${(props) => props.background};
    color: ${(props) => props.color};
    cursor: pointer;
  }

  .panel-inner {
    padding-bottom: 16px;
  }

  a {
    color: inherit;
  }
`

const MobileCvGrid = styled.div`
  padding: 8px 0 64px;

  /* ✅ removed the Experience label entirely (no .cv-title needed) */

  .row {
    border-top: 1px solid ${(props) => props.color};
    padding: 18px 0;
  }

  /* ✅ remove top border from first work experience entry */
  .row:first-of-type {
    border-top: none;
    padding-top: 0;
  }

  .exp-title {
    margin: 0 0 2px;
    line-height: 1.35;
  }

  .exp-company-dates {
    margin: 0 0 10px;
    opacity: 0.95;
    line-height: 1.35;
  }

  .exp-overview {
    margin-bottom: 12px;
  }

  .exp-overview p {
    margin: 0;
  }

  .exp-bullets ul,
  .exp-bullets ol {
    margin: 0;
    padding-left: 14px;
  }

  .exp-bullets li {
    margin: 0 10px 10px;
  }
`

const Cv = () => {
  const [pageData, setPageData] = useState(null)
  const [notFound, toggleNotFound] = useState(false)
  const [authed, setAuthed] = useState(() => isAuthedClient())
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT)

  const inputId = useMemo(() => 'cv-password', [])

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [aboutRes, experienceRes, siteSettingsRes] = await Promise.allSettled([
          client.getByUID('about', 'about'),
          client.getSingle('experience'),
          client.query(Prismic.Predicates.at('document.type', 'site_settings')),
        ])

        const about = aboutRes.status === 'fulfilled' ? aboutRes.value : null
        const experience = experienceRes.status === 'fulfilled' ? experienceRes.value : null
        const siteSettings = siteSettingsRes.status === 'fulfilled' ? siteSettingsRes.value : null

        if (!experience) {
          console.warn('CV page missing required Prismic single type: experience', experienceRes)
          toggleNotFound(true)
          return
        }

        // keep shape compatible with existing usage
        setPageData([about, experience, siteSettings])
      } catch (e) {
        console.warn('CV page fetch error:', e)
        toggleNotFound(true)
      }
    }

    fetchData()
  }, [])

  /**
   * ✅ MOBILE DEFAULT STATE:
   * Start with CV (exp) OPEN and bio CLOSED.
   */
  useEffect(() => {
    if (!isMobile) return
    if (!pageData) return

    const bio = document.querySelector('.cv-mobile-bio')
    const exp = document.querySelector('.cv-mobile-exp')
    if (!bio || !exp) return

    const setHeights = () => {
      const h = window.innerHeight

      removeClass(bio, 'expanded')
      addClass(exp, 'expanded')

      gsap.set(bio, { height: MIN_HEIGHT })
      gsap.set(exp, { height: h - MIN_HEIGHT })
    }

    setHeights()
    window.addEventListener('resize', setHeights)
    return () => window.removeEventListener('resize', setHeights)
  }, [isMobile, pageData])

  const toggleMobilePanel = (panel) => {
    const bio = document.querySelector('.cv-mobile-bio')
    const exp = document.querySelector('.cv-mobile-exp')
    if (!bio || !exp) return

    const windowHeight = window.innerHeight

    if (panel === 'bio') {
      addClass(bio, 'expanded')
      removeClass(exp, 'expanded')
      gsap.to(exp, { duration: 0.35, height: MIN_HEIGHT })
      gsap.to(bio, { duration: 0.35, height: windowHeight - MIN_HEIGHT })
    } else {
      addClass(exp, 'expanded')
      removeClass(bio, 'expanded')
      gsap.to(bio, { duration: 0.35, height: MIN_HEIGHT })
      gsap.to(exp, { duration: 0.35, height: windowHeight - MIN_HEIGHT })
    }
  }

  if (notFound) return <NotFound />
  if (!pageData) return null

  const about = pageData[0]
  const experienceDoc = pageData[1]
  const siteSettings = get(pageData[2], 'results[0].data', {})
  const aboutBio = getVariantField(get(about, 'data', {}), 'bio')
  const hasVariantBio = hasVariantField(get(about, 'data', {}), 'bio')

  const leftBg = getThemeBackground(siteSettings)
  const leftText = getThemeColor(siteSettings)
  const rightBg = getThemeBackground(siteSettings)
  const rightText = getThemeColor(siteSettings)

  const experienceItems = filterExperienceForCurrentVariant(
    get(experienceDoc, 'data.experience', []),
  )

  // ✅ contact links live on the Experience single doc
  const contactLinks = get(experienceDoc, 'data.contact_links', null)

  const siteTitle = RichText.asText(get(siteSettings, 'site_title', '')) || 'Jordan Philips'
  const useBgImage = shouldUseSiteBackgroundImage(
    siteSettings,
    get(siteSettings, 'use_site_background_image', ''),
  )
  const bgImageUrl = get(siteSettings, 'site_background_image.url', '')
  const themeClassName = isLightTheme(siteSettings) ? ' theme-light' : ''

  const LeftColumnContent = (
    <>
      <p className="column-header border-bottom">
        <span className="first"><a href="../">Jordan</a></span>
        <span className="last"> <a href="../">Philips</a></span>
      </p>

      <div className="column-inner-content">
        <div className={`bio${hasVariantBio ? ' variant-bio' : ''}`}>
          {aboutBio && <RichText render={aboutBio} />}
        </div>

        {contactLinks && (
          <div className="clients border-top">
            <RichText render={contactLinks} />
          </div>
        )}

        <div className="clients border-top">
          <a href="../">
            Back ↖
          </a>
        </div>

        {!authed && (
          <PasswordGate
            inputId={inputId}
            textColor={leftText}
            expectedPassword={get(siteSettings, 'site_password', '')}
            onSuccess={() => {
              setAuthed(true)
              window.location.reload()
            }}
          />
        )}
      </div>
    </>
  )

  // MOBILE
  if (isMobile) {
    return (
      <StyledPage className={`page-container${themeClassName}`} background={leftBg}>
        <Helmet>
          <title>{siteTitle} — CV</title>
          <link rel="icon" href="/favicon.png" />
        </Helmet>

        {useBgImage && (
          <StyledSiteBackground className="site-background" background={bgImageUrl} />
        )}

        <MobileWrap>
          <MobilePanel className="cv-mobile-bio column-bio" background={leftBg} color={leftText}>
            <div className="panel-header border-bottom" onClick={() => toggleMobilePanel('bio')}>
              <span className="first">Jordan</span>
              <span className="last"> Philips</span>
            </div>

            <div className="panel-inner">
              <div className={`bio${hasVariantBio ? ' variant-bio' : ''}`}>
                {aboutBio && <RichText render={aboutBio} />}
              </div>

              {contactLinks && (
                <div className="clients border-top">
                  <RichText render={contactLinks} />
                </div>
              )}

              <div className="clients border-top">
                <a href="../">
                  Back ↖
                </a>
              </div>

              {!authed && (
                <PasswordGate
                  inputId={inputId}
                  textColor={leftText}
                  expectedPassword={get(siteSettings, 'site_password', '')}
                  onSuccess={() => {
                    setAuthed(true)
                    window.location.reload()
                  }}
                />
              )}
            </div>
          </MobilePanel>

          <MobilePanel className="cv-mobile-exp" background={rightBg} color={rightText}>
            <div className="panel-header" onClick={() => toggleMobilePanel('exp')}>
              CV ↗
            </div>

            {authed && (
              <MobileCvGrid color={rightText}>
                {experienceItems.map((item, i) => {
                  const title = get(item, 'title', '').trim()
                  const company = get(item, 'company_name', '').trim()
                  const dates = get(item, 'date_range', '').trim()

                  return (
                    <div className="row" key={i}>
                      <div className="exp-title">{title}</div>
                      <div className="exp-company-dates">
                        {company}
                        {company && dates ? ', ' : ''}
                        {dates}
                      </div>

                      {get(item, 'overview', null) && (
                        <div className="exp-overview">
                          <RichText render={get(item, 'overview', [])} />
                        </div>
                      )}

                      {get(item, 'bullets', null) && (
                        <div className="exp-bullets">
                          <RichText render={get(item, 'bullets', [])} />
                        </div>
                      )}
                    </div>
                  )
                })}
              </MobileCvGrid>
            )}
          </MobilePanel>
        </MobileWrap>
      </StyledPage>
    )
  }

  // DESKTOP
  return (
    <StyledPage className={`page-container${themeClassName}`} background={leftBg}>
      <Helmet>
        <title>{siteTitle} — CV</title>
        <link rel="icon" href="/favicon.png" />
      </Helmet>

      {useBgImage && (
        <StyledSiteBackground className="site-background" background={bgImageUrl} />
      )}

      <DesktopWrap>
        <StyledColumn className="column column-bio" background={leftBg} color={leftText}>
          {LeftColumnContent}
        </StyledColumn>

        {authed && (
          <CvColumnDesktop className="column" background={rightBg} color={rightText}>
            <div className="cv-inner">
              <div className="cv-grid">
                <div className="cv-title">Experience</div>

                {experienceItems.map((item, i) => {
                  const title = get(item, 'title', '').trim()
                  const company = get(item, 'company_name', '').trim()
                  const dates = get(item, 'date_range', '').trim()

                  return (
                    <div className="row-grid" key={i}>
                      <div className="exp-left">
                        <div className="exp-title">{title}</div>
                        <div className="exp-company-dates">
                          {company}
                          {company && dates ? ', ' : ''}
                          {dates}
                        </div>

                        {get(item, 'overview', null) && (
                          <div className="exp-overview">
                            <RichText render={get(item, 'overview', [])} />
                          </div>
                        )}
                      </div>

                      <div className="exp-bullets">
                        {get(item, 'bullets', null) && (
                          <RichText render={get(item, 'bullets', [])} />
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </CvColumnDesktop>
        )}
      </DesktopWrap>
    </StyledPage>
  )
}

export default Cv
